import { SystemProps, extendTheme } from "@chakra-ui/react";

/**
 * CLE color template — `cle.*` is the stable brand scale (logo ring + wordmark).
 * UI currently uses `preview.*` (MCP earthy palette, base #1E3A2F) for surfaces, buttons, and nav;
 * `cle.*` values are unchanged for reference and gradual migration.
 */
type Colors = {
  lp_grey: {
    [key: number]: string;
  };
  cle: {
    cream: string;
    beige: string;
    leaf: string;
    forest: string;
    forestHover: string;
    sky: string;
    amber: string;
    coral: string;
    charcoal: string;
    iconWell: string;
    muted: string;
    white: string;
  };
  /** Temporary MCP palette for UI — does not replace `cle.*` brand tokens */
  preview: {
    base: string;
    primary: string;
    secondary: string;
    accent: string;
    neutral: string;
    background: string;
  };
};

export interface CustomLinkProps extends SystemProps {
  theme: {
    colors: Colors;
  };
}

/** Matches `cle.forest` — for raw CSS strings (box-shadow, etc.) */
export const CLE_FOREST_HEX = "#4F6F34";

/** RGB for cle.forest — use in rgba() borders/shadows: `rgba(${CLE_FOREST_RGB}, α)` */
export const CLE_FOREST_RGB = "79, 111, 52";

/** Matches `preview.primary` — for raw CSS strings when the preview palette is applied */
export const PREVIEW_PRIMARY_HEX = "#3e926f";

/** RGB for preview.primary — use in rgba() with the temporary preview palette */
export const PREVIEW_PRIMARY_RGB = "62, 146, 111";

const theme = extendTheme({
  colors: {
    lp_grey: {
      50: "#f7f7f7",
      100: "#eeeeee",
      200: "#cfcfcf",
      300: "#b1b1b1",
      400: "#9e9e9e",
      500: "#55565b",
      600: "#434343",
      700: "#383838",
      800: "#303030",
      900: "#1c1c1c",
    },
    cle: {
      cream: "#FAF9F5",
      beige: "#F0ECE4",
      leaf: "#6FB83A",
      forest: "#4F6F34",
      forestHover: "#425C2B",
      sky: "#5C8CC9",
      amber: "#D4A012",
      coral: "#DD6048",
      charcoal: "#2F322E",
      iconWell: "#E4EDE0",
      muted: "#5A605C",
      white: "#FFFFFF",
    },
    preview: {
      base: "#1E3A2F",
      primary: "#3e926f",
      secondary: "#52a46d",
      accent: "#894583",
      neutral: "#4b7474",
      background: "#f0f2f3",
    },
  } satisfies Colors,
  fonts: {
    heading: `'EB Garamond', 'Georgia', 'Times New Roman', serif`,
    body: `'Inter', system-ui, -apple-system, sans-serif`,
    lato: `'lato', sans-serif`,
  },
  styles: {
    global: {
      body: {
        bg: "preview.background",
        color: "cle.charcoal",
        fontFamily: "body",
      },
    },
  },
  components: {
    Heading: {
      baseStyle: {
        fontFamily: "heading",
        color: "preview.neutral",
      },
    },
    Button: {
      variants: {
        cleSolid: {
          bg: "preview.primary",
          color: "cle.white",
          borderRadius: "full",
          px: 8,
          py: 6,
          fontWeight: 500,
          _hover: {
            bg: "preview.secondary",
            _disabled: { bg: "preview.primary" },
          },
        },
        cleOutline: {
          bg: "transparent",
          color: "preview.primary",
          borderWidth: "1px",
          borderColor: "preview.primary",
          borderRadius: "full",
          px: 8,
          py: 6,
          fontWeight: 500,
          _hover: {
            bg: "cle.beige",
          },
        },
      },
    },
  },
});

export default theme;
